/**
 * The screens, routed on the path: the Play tab at /, the Characters tab
 * under /characters, a game at /g/<id>, one's own account at /account, and
 * for an admin the accounts under /accounts and players' problem reports
 * under /reports.
 */
export type Route =
  | {
      readonly screen: "play";
      /** A saved character to start with, from ?character=<id>. */ readonly pick: string | null;
    }
  | { readonly screen: "game"; readonly id: string }
  /** A table gathering before its game begins: the host's own, or (with its word) one someone was asked to. */
  | { readonly screen: "table"; readonly id: string; readonly code: string | null }
  /** Asked to the server itself, with a link that lets one make an account. */
  | { readonly screen: "invite"; readonly code: string }
  /** Asked to someone's table: the game, and the word its host gave out. */
  | { readonly screen: "join"; readonly id: string; readonly code: string }
  | { readonly screen: "characters"; readonly id: string | null; readonly mode: "view" | "edit" | "new" }
  | { readonly screen: "account" }
  /** How to play here, for someone new. */
  | { readonly screen: "guide"; readonly page: string | null }
  /** Where the game stands and where it is going. */
  | { readonly screen: "roadmap" }
  | { readonly screen: "accounts"; readonly id: string | null; readonly mode: "view" | "new" }
  | { readonly screen: "reports"; readonly id: string | null }
  | { readonly screen: "review"; readonly id: string | null }
  | { readonly screen: "terms" }
  /** The vaults, for an admin: a folder or a note, by its path under them ("" is the top). */
  | { readonly screen: "vaults"; readonly path: string }
  /** An address that is none of these: said so, with the way to Play. */
  | { readonly screen: "missing" };

/** `location` is a path with its query, if any. The top is the Play tab; anything unknown is said to be missing. */
export function routeOf(location: string): Route {
  const [path = "/", query = ""] = location.split("?");
  const parts = path.split("/").filter(Boolean).map(decodeURIComponent);
  if (parts[0] === "g" && parts[1] && parts.length === 2) return { screen: "game", id: parts[1] };
  if (parts[0] === "invite" && parts[1] && parts.length === 2) return { screen: "invite", code: parts[1] };
  if (parts[0] === "table" && parts[1] && parts.length <= 3) {
    return { screen: "table", id: parts[1], code: parts[2] ?? null };
  }
  if (parts[0] === "join" && parts[1] && parts[2] && parts.length === 3) {
    return { screen: "join", id: parts[1], code: parts[2] };
  }
  if (parts[0] === "characters") {
    if (parts.length === 1) return { screen: "characters", id: null, mode: "view" };
    if (parts[1] === "new" && parts.length === 2) return { screen: "characters", id: null, mode: "new" };
    if (parts.length === 2) return { screen: "characters", id: parts[1]!, mode: "view" };
    if (parts[2] === "edit" && parts.length === 3) return { screen: "characters", id: parts[1]!, mode: "edit" };
  }
  if (parts[0] === "account" && parts.length === 1) return { screen: "account" };
  if (parts[0] === "guide" && parts.length <= 2) return { screen: "guide", page: parts[1] ?? null };
  if (parts[0] === "roadmap" && parts.length === 1) return { screen: "roadmap" };
  if (parts[0] === "terms" && parts.length === 1) return { screen: "terms" };
  if (parts[0] === "reports" && parts.length <= 2) return { screen: "reports", id: parts[1] ?? null };
  if (parts[0] === "review" && parts.length <= 2) return { screen: "review", id: parts[1] ?? null };
  if (parts[0] === "vaults") return { screen: "vaults", path: parts.slice(1).join("/") };
  if (parts[0] === "accounts") {
    if (parts.length === 1) return { screen: "accounts", id: null, mode: "view" };
    if (parts[1] === "new" && parts.length === 2) return { screen: "accounts", id: null, mode: "new" };
    if (parts.length === 2) return { screen: "accounts", id: parts[1]!, mode: "view" };
  }
  if (parts.length === 0) return { screen: "play", pick: new URLSearchParams(query).get("character") };
  return { screen: "missing" };
}

export const gamePath = (id: string) => `/g/${encodeURIComponent(id)}`;
export const tablePath = (id: string, code?: string) =>
  `/table/${encodeURIComponent(id)}${code ? `/${encodeURIComponent(code)}` : ""}`;
export const invitePath = (code: string) => `/invite/${encodeURIComponent(code)}`;
export const joinPath = (id: string, code: string) => `/join/${encodeURIComponent(id)}/${encodeURIComponent(code)}`;
export const characterPath = (id: string) => `/characters/${encodeURIComponent(id)}`;
export const editPath = (id: string) => `${characterPath(id)}/edit`;
export const playPath = (characterId?: string) =>
  characterId ? `/?character=${encodeURIComponent(characterId)}` : "/";
export const NEW_CHARACTER = "/characters/new";
export const CHARACTERS = "/characters";
export const ACCOUNT = "/account";
export const GUIDE = "/guide";
/** A page of the guide; the landing page is the guide itself. */
export const guidePath = (page: string) => (page === "quick-start" ? GUIDE : `${GUIDE}/${page}`);
export const ROADMAP = "/roadmap";
export const ACCOUNTS = "/accounts";
export const NEW_ACCOUNT = "/accounts/new";
export const accountPath = (id: string) => `/accounts/${encodeURIComponent(id)}`;
export const REPORTS = "/reports";
/** For admins: what the server turned away, an account and a day to a ticket. */
export const REVIEW = "/review";
export const reviewPath = (id: string) => `/review/${encodeURIComponent(id)}`;
export const VAULTS = "/vaults";
export const vaultPath = (path: string) =>
  `/vaults${path ? `/${path.split("/").map(encodeURIComponent).join("/")}` : ""}`;
export const reportPath = (id: string) => `/reports/${encodeURIComponent(id)}`;
